import {
  BareCommunity,
  BareLeague,
  BareTeam,
  Community,
  ContractStatus,
  ContractWithTeamInfo,
  ContractWithUserInfo,
  GivenAward,
  Leaderboard,
  League,
  MatchStatus,
  NoResponse,
  OfferContractRequest,
  PageDetailedMatch,
  PageLeaderboardRow,
  RescheduleRequestWithTeam,
  StreamContentPydanticModel,
  Team,
  TeamIsPremium,
  TeamRecord,
  TeamsApi,
  TransferContractRequest,
} from "@virtualprogaming/js-public-sdk";
import { AxiosResponse } from "axios";
import { isFunction } from "lodash-es";
import { useMemo } from "react";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "react-query";

import { environment } from "~/environment";
import { useApiConfig } from "~/utils";
import { useAuth } from "~/utils";

export function useTeamApi() {
  const config = useApiConfig();
  return useMemo(() => new TeamsApi(...config), [config]);
}

export function useTeam(slug: string, options?: UseQueryOptions<Team>) {
  const api = useTeamApi();
  return useQuery({
    queryKey: ["teams", slug],
    queryFn: async () => {
      const { data } = await api.getTeamBySlug(slug);
      return data;
    },
    ...options,
  });
}

export function useTeamIsPremium(
  slug: string,
  options?: UseQueryOptions<TeamIsPremium>
) {
  const api = useTeamApi();
  return useQuery({
    queryKey: ["teams", slug, "premium"],
    queryFn: async () => {
      const { data } = await api.isTeamPremium(slug);
      return data;
    },
    ...options,
  });
}

export function useTeamCommunity(
  slug: string,
  options?: UseQueryOptions<Community>
) {
  const api = useTeamApi();
  return useQuery({
    queryKey: ["teams", slug, "community"],
    queryFn: async () => {
      const { data } = await api.getTeamCommunity(slug);
      return data;
    },
    ...options,
  });
}

export function useTeamCommunities(
  slug: string,
  options?: UseQueryOptions<BareCommunity[]>
) {
  const api = useTeamApi();
  return useQuery({
    queryKey: ["teams", slug, "communities"],
    queryFn: async () => {
      const { data } = await api.getTeamCommunities(slug);
      return data;
    },
    ...options,
  });
}

export function useTeamLeague(slug: string, options?: UseQueryOptions<League>) {
  const api = useTeamApi();
  return useQuery({
    queryKey: ["teams", slug, "league"],
    queryFn: async () => {
      const { data } = await api.getTeamLeague(slug);
      return data;
    },
    ...options,
  });
}

export function useTeamMatches(
  slug: string,
  matchStatus: MatchStatus,
  season?: number,
  leagueId?: number,
  tournamentId?: number,
  matchDay?: number,
  datetime?: string,
  limit?: number,
  offset?: number,
  options?: UseQueryOptions<PageDetailedMatch>
) {
  const api = useTeamApi();
  return useQuery({
    queryKey: [
      "teams",
      "matches",
      slug,
      matchStatus,
      datetime,
      leagueId,
      tournamentId,
      season,
      { limit, offset },
    ],
    queryFn: async () => {
      const { data } = await api.getTeamMatchesByStatusPaged(
        slug,
        matchStatus,
        leagueId,
        tournamentId,
        season,
        matchDay,
        datetime,
        limit,
        offset
      );
      return data;
    },
    ...options,
  });
}

export function useTeamContracts(
  slug: string,
  leagueSlug?: string,
  status?: ContractStatus,
  options?: UseQueryOptions<ContractWithUserInfo[]>
) {
  const api = useTeamApi();
  return useQuery({
    queryKey: ["teamContracts", slug, leagueSlug, status],
    queryFn: async () => {
      const { data } = await api.getContracts(slug, leagueSlug, status);
      return data;
    },
    ...options,
  });
}

export function useContractsTeamLeague(
  slug: string,
  league: string,
  contractStatus: ContractStatus,
  options?: UseQueryOptions<ContractWithUserInfo[]>
) {
  const api = useTeamApi();
  return useQuery({
    queryKey: [
      "teamContracts",
      "leagueContracts",
      contractStatus,
      league,
      slug,
    ],
    queryFn: async () => {
      const { data } = await api.getContracts(slug, league, contractStatus);
      return data;
    },
    ...options,
  });
}

export function useTeamTransfers(
  slug: string,
  options?: UseQueryOptions<ContractWithTeamInfo[]>
) {
  const api = useTeamApi();
  return useQuery({
    queryKey: ["teamTransfers", slug],
    queryFn: async () => {
      const { data } = await api.getTransfers(slug);
      return data;
    },
    ...options,
  });
}

export function useTeamRescheduleRequests(
  slug: string,
  options?: UseQueryOptions<RescheduleRequestWithTeam[]>
) {
  const api = useTeamApi();
  return useQuery({
    queryKey: ["teamRescheduleRequests", slug],
    queryFn: async () => {
      const { data } = await api.getTeamRescheduleRequests(slug);
      return data;
    },
    ...options,
  });
}

export function useTeamTrophies(
  slug: string,
  options?: UseQueryOptions<GivenAward[]>
) {
  const api = useTeamApi();
  return useQuery({
    queryKey: ["teams", slug, "trophies"],
    queryFn: async () => {
      const { data } = await api.getTeamTrophies(slug);
      return data;
    },
    ...options,
  });
}

export function useTeamRecord(
  slug: string,
  options?: UseQueryOptions<TeamRecord>
) {
  const api = useTeamApi();
  return useQuery({
    queryKey: ["teams", slug, "record"],
    queryFn: async () => {
      const { data } = await api.getTeamRecord(slug);
      return data;
    },
    ...options,
  });
}

export function useTeamStreams(
  slug: string,
  options?: UseQueryOptions<StreamContentPydanticModel[]>
) {
  const api = useTeamApi();
  return useQuery({
    queryKey: ["teams", slug, "streams"],
    queryFn: async () => {
      const { data } = await api.getTeamStreams(slug);
      return data;
    },
    ...options,
  });
}

export function useTeamSeasons(
  slug: string,
  options?: UseQueryOptions<number[]>
) {
  const api = useTeamApi();
  return useQuery({
    queryKey: ["teams", slug, "seasons"],
    queryFn: async () => {
      const { data } = await api.getTeamSeasons(slug);
      return data;
    },
    ...options,
  });
}

export function useTeamLeaderboard(
  slug: string,
  leaderboard: Leaderboard,
  season?: number,
  weekly = false,
  limit?: number,
  offset?: number,
  options?: UseQueryOptions<PageLeaderboardRow>
) {
  const api = useTeamApi();
  return useQuery({
    queryKey: [
      "teams",
      slug,
      "leaderboard",
      season,
      leaderboard,
      weekly,
      { limit, offset },
    ],
    queryFn: async () => {
      const { data } = await api.getTeamLeaderboardPaged(
        slug,
        leaderboard,
        weekly,
        season,
        limit,
        offset
      );
      return data;
    },
    ...options,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    cacheTime: 1 * 60 * 60 * 1000,
    staleTime: 1 * 60 * 60 * 1000,
    retry: 1,
  });
}

export type TUpdateTeamAssistants = [string, number[]];

export function useTeamAssistantsUpdate(
  options?: UseMutationOptions<
    AxiosResponse<NoResponse>,
    unknown,
    TUpdateTeamAssistants
  >
) {
  const api = useTeamApi();
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options || {};
  return useMutation((vars) => api.updateAssistants(...vars), {
    onSuccess: async (data, vars, ctx) => {
      isFunction(onSuccess) && onSuccess(data, vars, ctx);
      await queryClient.invalidateQueries(["teamContracts"]);
    },
    ...rest,
  });
}

type TOfferContract = [slug: string, request: OfferContractRequest];

export function useTeamContractOffer(
  options?: UseMutationOptions<
    AxiosResponse<NoResponse>,
    unknown,
    TOfferContract
  >
) {
  const api = useTeamApi();
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options || {};
  return useMutation((rq) => api.offerContract(...rq), {
    onSuccess: async (data, vars, ctx) => {
      isFunction(onSuccess) && onSuccess(data, vars, ctx);
      await queryClient.invalidateQueries(["teams", "contracts"]);
    },
    ...rest,
  });
}

type TOfferTransfer = [
  slug: string,
  originalContractId: number,
  request: TransferContractRequest
];

export function useTeamTransferOffer(
  options?: UseMutationOptions<
    AxiosResponse<NoResponse>,
    unknown,
    TOfferTransfer
  >
) {
  const api = useTeamApi();
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options || {};
  return useMutation((rq) => api.offerTransfer(...rq), {
    onSuccess: async (data, vars, ctx) => {
      isFunction(onSuccess) && onSuccess(data, vars, ctx);
      await queryClient.invalidateQueries(["teams", "contracts"]);
    },
    ...rest,
  });
}

export function useTeamLeagues(
  slug: string,
  options?: UseQueryOptions<BareLeague[]>
) {
  const api = useTeamApi();
  return useQuery({
    queryKey: ["teams", slug, "leagues"],
    queryFn: async () => {
      const { data } = await api.getTeamLeagues(slug);
      return data;
    },
    ...options,
  });
}

export const useTeamInfoById = (
  id: number,
  options?: UseQueryOptions<BareTeam>
) => {
  const api = useTeamApi();
  return useQuery({
    queryKey: ["teamInfo", id],
    queryFn: async () => (await api.getTeaminfosById(id)).data,
    ...options,
  });
};

//  update EA club via fetch (not the SDK) to avoid "updateEaClub" missing on TeamsApi
type UpdateEAClubVars = { teamId: number; eaClubName: string };

interface UpdateEAClubError {
  status?: number;
  [key: string]: unknown;
}

export function useUpdateEAClub(
  options?: UseMutationOptions<void, UpdateEAClubError, UpdateEAClubVars>
) {
  const [, publicBase] = useApiConfig();
  const { access: token } = useAuth();
  const qc = useQueryClient();

  // Using from environment. Maybe Nabel can find another way
  const eaSecret = environment.EA_SECRET;

  return useMutation<void, UpdateEAClubError, UpdateEAClubVars>(
    async ({ teamId, eaClubName }) => {
      const base = (publicBase || "").replace(/\/$/, "");
      const url = `${base}/teams/eaclub/${teamId}`;
      const res = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({ ea_club_name: eaClubName, token: eaSecret }),
      });
      if (!res.ok) {
        let data: UpdateEAClubError = {};
        try {
          data = (await res.json()) as UpdateEAClubError;
        } catch {
          /* no-op */
        }
        data.status = res.status;
        throw data;
      }
    },
    {
      async onSuccess() {
        await qc.invalidateQueries("teams");
      },
      ...options,
    }
  );
}
